/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  GLOSSARY.TSX - Interactive Help & Glossary Panel                         ║
 * ║  Searchable in-app help for all game concepts                             ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface GlossaryEntry {
  term: string;
  definition: string;
  category: 'basics' | 'mechanics' | 'events' | 'teams' | 'strategy';
  icon: string;
  related?: string[];
}

// Comprehensive glossary database
const GLOSSARY: GlossaryEntry[] = [
  // BASICS
  {
    term: 'Keys',
    definition: 'Keys are your tickets to earning in NoTomorrow3D. Each key you own gives you a share of dividends from every purchase. Keys also make you eligible to win the pot if you\'re the last buyer!',
    category: 'basics',
    icon: '🔑',
    related: ['Dividends', 'Key Price', 'Bonding Curve'],
  },
  {
    term: 'Timer',
    definition: 'The countdown clock at the heart of the game. When it hits zero, the game ends and the last buyer wins! Each key purchase adds 30 seconds to the timer (up to 24 hours max).',
    category: 'basics',
    icon: '⏱️',
    related: ['Last Buyer', 'Timer Extension', 'Reaper Key'],
  },
  {
    term: 'Prize Pot',
    definition: 'The total PAW accumulated from purchases. 45% of every purchase goes into the pot. When the timer hits zero, 48% of the pot goes to the winner, 35% to dividends, and the rest rolls over.',
    category: 'basics',
    icon: '💰',
    related: ['Winner', 'Dividends', 'Fee Distribution'],
  },
  {
    term: 'Last Buyer',
    definition: 'The player who made the most recent key purchase. If the timer hits zero while you\'re the last buyer, you win the pot!',
    category: 'basics',
    icon: '🏆',
    related: ['Timer', 'Prize Pot', 'Winning'],
  },
  {
    term: 'Dividends',
    definition: 'Passive income! 35% of every purchase is distributed to all key holders proportionally. The more keys you own, the bigger your share.',
    category: 'basics',
    icon: '💎',
    related: ['Keys', 'Fee Distribution', 'Passive Income'],
  },
  {
    term: 'PAW',
    definition: 'The native token of PawChain and the currency used in NoTomorrow3D. All purchases, winnings, and dividends are in PAW.',
    category: 'basics',
    icon: '🐾',
    related: ['Token', 'Currency'],
  },

  // MECHANICS
  {
    term: 'Key Price',
    definition: 'The current cost to buy one key. Price follows a bonding curve - it starts low and increases as more keys are sold. Early buyers get better prices!',
    category: 'mechanics',
    icon: '📈',
    related: ['Bonding Curve', 'Keys'],
  },
  {
    term: 'Bonding Curve',
    definition: 'A mathematical formula that determines key prices. Price = Base Price + (Increment × Total Keys Sold). This creates fair pricing where early supporters benefit.',
    category: 'mechanics',
    icon: '📊',
    related: ['Key Price', 'Economics'],
  },
  {
    term: 'Timer Extension',
    definition: 'Each key purchase adds 30 seconds to the timer (up to 24 hours max). This prevents the game from ending too quickly and gives everyone a chance.',
    category: 'mechanics',
    icon: '⏰',
    related: ['Timer', 'Keys', 'Ghost Key'],
  },
  {
    term: 'Fee Distribution',
    definition: 'How purchases are split: 45% to pot, 35% to dividends, 5% referrals, 5% jackpot pool, 5% rollover, 3% team jackpot, 2% community.',
    category: 'mechanics',
    icon: '📊',
    related: ['Dividends', 'Prize Pot', 'Referrals'],
  },
  {
    term: 'Combo System',
    definition: 'Buy within 60 seconds of your last purchase to build a combo! 2x = +5% bonus keys, 3x = +10%, 4x+ = +15%. Combos reset after 60 seconds of inactivity.',
    category: 'mechanics',
    icon: '⚡',
    related: ['Bonus Keys', 'Strategy'],
  },
  {
    term: 'Volume Bonus',
    definition: 'Bigger purchases get bonus keys! 10M+ PAW = +1%, 50M+ = +2%, 100M+ = +3%, 500M+ = +5%, 1B+ = +8%.',
    category: 'mechanics',
    icon: '💎',
    related: ['Bonus Keys', 'Raven'],
  },
  {
    term: 'Referral System',
    definition: 'Share your referral link and earn 5% of all purchases made by players who use it. Passive income from your network!',
    category: 'mechanics',
    icon: '🔗',
    related: ['Earnings', 'Sharing'],
  },
  {
    term: 'Winner\'s Curse',
    definition: 'The previous round\'s winner is "cursed" - their purchases extend the timer by 2x (60 seconds instead of 30). The curse lasts one round.',
    category: 'mechanics',
    icon: '👻',
    related: ['Winner', 'Timer Extension'],
  },

  // SPECIAL KEYS
  {
    term: 'Reaper Key',
    definition: 'A powerful attack! Reaper Keys cost 5x the current key price and slash 30 seconds off the timer. 50% goes to pot, 40% to dividends. Use when timer is high!',
    category: 'mechanics',
    icon: '💀',
    related: ['Timer', 'Strategy', 'Attack'],
  },
  {
    term: 'PHANTOM',
    definition: 'THALIA\'s sneaky trick! There\'s a 1-10% chance any purchase becomes a phantom — it doesn\'t extend the timer! Completely random and unpredictable.',
    category: 'mechanics',
    icon: '👻',
    related: ['Timer Extension', 'Random', 'THALIA'],
  },

  // THALIA COUNTERMEASURES
  {
    term: 'SURGE',
    definition: 'THALIA escalates! Every 10th round activates SURGE: 2x dividends, 75% to winner (up from 48%), 12-hour timer (instead of 24hr). High risk, high reward!',
    category: 'events',
    icon: '🔴',
    related: ['Round', 'Dividends', 'Special'],
  },
  {
    term: 'RECALIBRATION',
    definition: 'THALIA countermeasure! Every 3-9 hours, the timer drops by 5-30%. Creates sudden urgency and buying opportunities.',
    category: 'events',
    icon: '⏱️',
    related: ['Timer', 'THALIA', 'Random'],
  },
  {
    term: 'TERMINAL',
    definition: 'Activates when THALIA\'s timer drops below 5-15 minutes. Keys cost 50% less, but 50% goes to dividends instead of pot. Endgame frenzy!',
    category: 'events',
    icon: '🎯',
    related: ['Timer', 'Endgame', 'Strategy'],
  },
  {
    term: 'GLITCH',
    definition: 'THALIA\'s systems glitch! Surprise 50% discount on keys for 60 seconds! Exploit the glitch fast!',
    category: 'events',
    icon: '⚡',
    related: ['Discount', 'Key Price', 'Opportunity'],
  },
  {
    term: 'AMPLIFICATION',
    definition: 'When a raven buys 500+ keys, THALIA amplifies FOMO — prices surge to 2x for 30 seconds!',
    category: 'events',
    icon: '📈',
    related: ['Raven', 'Key Price', 'FOMO'],
  },
  {
    term: 'ERUPTION',
    definition: '2% chance per purchase to trigger an eruption that redistributes 10% of the pot to all key holders!',
    category: 'events',
    icon: '🌋',
    related: ['Random', 'Dividends', 'Bonus'],
  },
  {
    term: 'Charity Chaos',
    definition: 'Random event where 7% of the pot is redistributed to the 10 smallest key holders. Helps the little guys!',
    category: 'events',
    icon: '💝',
    related: ['Redistribution', 'Small Holders'],
  },
  {
    term: 'CARNIVAL',
    definition: 'Every 25th round is a CARNIVAL with 3 random chaos effects active simultaneously! Total mayhem.',
    category: 'events',
    icon: '🎪',
    related: ['Round', 'Special', 'Multiple Effects'],
  },

  // FACTIONS (THALIA narrative)
  {
    term: 'Factions',
    definition: 'There are 8 resistance factions: Raven, Ape, Moth, Viper, Rabbit, Wolf, Fox, and Owl. Each has unique powers to fight THALIA. Choose one when you buy your first key - you\'re locked in for the round!',
    category: 'teams',
    icon: '👥',
    related: ['Faction Jackpot', 'Resistance'],
  },
  {
    term: 'Faction Jackpot',
    definition: '3% of all purchases go to the Faction Jackpot pool. At round end, the winning faction (faction with the winner) splits the jackpot proportionally!',
    category: 'teams',
    icon: '🎉',
    related: ['Factions', 'Bonus', 'Winner'],
  },
  {
    term: 'Faction Lock',
    definition: 'Once you buy your first key in a round, you\'re locked to that faction until the round ends. Choose wisely!',
    category: 'teams',
    icon: '🔒',
    related: ['Factions', 'Strategy'],
  },
  {
    term: 'Faction Powers',
    definition: 'Each faction has unique abilities to counter THALIA. Use them strategically!',
    category: 'teams',
    icon: '⚡',
    related: ['Factions', 'Strategy', 'Special'],
  },

  // STRATEGY
  {
    term: 'Raven',
    definition: 'A player who makes very large purchases (500+ keys). Ravens can trigger AMPLIFICATION and move markets!',
    category: 'strategy',
    icon: '🐦‍⬛',
    related: ['Big Buyer', 'Market Moving'],
  },
  {
    term: 'Sniping',
    definition: 'A strategy where you wait until THALIA\'s timer is very low before buying, trying to be the last buyer before the Singularity.',
    category: 'strategy',
    icon: '🎯',
    related: ['Endgame', 'Timer', 'Winning'],
  },
  {
    term: 'Dividend Farming',
    definition: 'A strategy focused on accumulating keys early for maximum dividend income, rather than trying to win the pot.',
    category: 'strategy',
    icon: '🌾',
    related: ['Dividends', 'Passive Income', 'Long-term'],
  },
  {
    term: 'Round',
    definition: 'One complete game from start to finish. A round ends when the timer hits zero. Past rounds\' winners and stats are recorded.',
    category: 'basics',
    icon: '🔄',
    related: ['Timer', 'Winner', 'History'],
  },
];

// Category icons
const CATEGORY_ICONS: Record<string, { icon: string; label: string; color: string }> = {
  basics: { icon: '📚', label: 'Basics', color: 'text-cyan-400' },
  mechanics: { icon: '⚙️', label: 'Mechanics', color: 'text-purple-400' },
  events: { icon: '🎭', label: 'Events', color: 'text-orange-400' },
  teams: { icon: '👥', label: 'Teams', color: 'text-green-400' },
  strategy: { icon: '🎯', label: 'Strategy', color: 'text-yellow-400' },
};

interface GlossaryProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Glossary({ isOpen, onClose }: GlossaryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<GlossaryEntry | null>(null);

  // Filter entries
  const filteredEntries = useMemo(() => {
    let entries = GLOSSARY;

    if (selectedCategory) {
      entries = entries.filter(e => e.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      entries = entries.filter(e => 
        e.term.toLowerCase().includes(query) ||
        e.definition.toLowerCase().includes(query)
      );
    }

    return entries.sort((a, b) => a.term.localeCompare(b.term));
  }, [searchQuery, selectedCategory]);

  // Handle related term click
  const handleRelatedClick = useCallback((term: string) => {
    const entry = GLOSSARY.find(e => e.term.toLowerCase() === term.toLowerCase());
    if (entry) {
      setSelectedEntry(entry);
      setSearchQuery('');
      setSelectedCategory(null);
    }
  }, []);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            className="w-full max-w-2xl max-h-[80vh] bg-gray-900/95 rounded-2xl border border-neon-cyan/30 overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b border-neon-cyan/20 bg-gradient-to-r from-neon-cyan/10 to-neon-magenta/10">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-orbitron text-neon-cyan flex items-center gap-2">
                  📖 Game Glossary
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>

              {/* Search */}
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search terms..."
                className="w-full bg-black/50 border border-neon-cyan/30 rounded-lg px-4 py-2 text-white font-rajdhani focus:outline-none focus:border-neon-cyan"
              />

              {/* Category filters */}
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`px-3 py-1 rounded-full text-xs font-rajdhani transition-colors ${
                    !selectedCategory
                      ? 'bg-neon-cyan text-black'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  All
                </button>
                {Object.entries(CATEGORY_ICONS).map(([key, { icon, label, color }]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedCategory(selectedCategory === key ? null : key)}
                    className={`px-3 py-1 rounded-full text-xs font-rajdhani transition-colors flex items-center gap-1 ${
                      selectedCategory === key
                        ? `bg-gray-700 ${color}`
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    {icon} {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="flex h-[calc(80vh-180px)]">
              {/* Term list */}
              <div className="w-1/3 border-r border-gray-700 overflow-y-auto">
                {filteredEntries.map((entry) => (
                  <button
                    key={entry.term}
                    onClick={() => setSelectedEntry(entry)}
                    className={`w-full px-4 py-3 text-left border-b border-gray-800 hover:bg-gray-800/50 transition-colors ${
                      selectedEntry?.term === entry.term ? 'bg-neon-cyan/10 border-l-2 border-l-neon-cyan' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{entry.icon}</span>
                      <span className="font-rajdhani text-white">{entry.term}</span>
                    </div>
                    <span className={`text-xs ${CATEGORY_ICONS[entry.category].color}`}>
                      {CATEGORY_ICONS[entry.category].label}
                    </span>
                  </button>
                ))}
                {filteredEntries.length === 0 && (
                  <div className="p-4 text-center text-gray-500 font-rajdhani">
                    No matching terms found
                  </div>
                )}
              </div>

              {/* Definition panel */}
              <div className="flex-1 p-6 overflow-y-auto">
                {selectedEntry ? (
                  <motion.div
                    key={selectedEntry.term}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-4xl">{selectedEntry.icon}</span>
                      <div>
                        <h3 className="text-2xl font-orbitron text-white">{selectedEntry.term}</h3>
                        <span className={`text-sm ${CATEGORY_ICONS[selectedEntry.category].color}`}>
                          {CATEGORY_ICONS[selectedEntry.category].icon} {CATEGORY_ICONS[selectedEntry.category].label}
                        </span>
                      </div>
                    </div>

                    <p className="text-gray-300 font-rajdhani text-lg leading-relaxed mb-6">
                      {selectedEntry.definition}
                    </p>

                    {selectedEntry.related && selectedEntry.related.length > 0 && (
                      <div>
                        <h4 className="text-sm font-orbitron text-gray-400 mb-2">Related Terms</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedEntry.related.map(term => (
                            <button
                              key={term}
                              onClick={() => handleRelatedClick(term)}
                              className="px-3 py-1 rounded-full bg-gray-800 text-neon-cyan text-sm hover:bg-gray-700 transition-colors"
                            >
                              {term}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 font-rajdhani">
                    <div className="text-center">
                      <span className="text-5xl block mb-4">📖</span>
                      Select a term to learn more
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Floating help button component with integrated glossary
export function HelpButton({ onClick, inline = false }: { onClick?: () => void; inline?: boolean } = {}) {
  const [isOpen, setIsOpen] = useState(false);
  
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      setIsOpen(true);
    }
  };

  if (inline) {
    return (
      <>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleClick}
          className="w-9 h-9 rounded-full bg-gradient-to-br from-neon-cyan to-neon-magenta shadow-lg flex items-center justify-center text-base hover:shadow-neon-cyan/50 transition-shadow"
          title="Open Glossary"
        >
          ❓
        </motion.button>
        <Glossary isOpen={isOpen} onClose={() => setIsOpen(false)} />
      </>
    );
  }

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleClick}
        className="fixed bottom-4 sm:bottom-6 left-24 sm:left-28 z-40 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-neon-cyan to-neon-magenta shadow-lg flex items-center justify-center text-xl sm:text-2xl hover:shadow-neon-cyan/50 transition-shadow"
        title="Open Glossary"
      >
        ❓
      </motion.button>
      <Glossary isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
}

export { GLOSSARY };
